import React from 'react';
import Article from './Article';
import Subheading from './Subheading';
import Graf from './Graf';
import { Paragraph } from 'scy/text';

export default function PrivacyPolicy() {
  return (
    <Article
      title="Privacy Policy"
      lastUpdated="December 31, 2019"
      asideTitle="contact"
      anchorTagText="support@saucey.com"
    >
      <Graf>
        This Privacy Policy describes Saucey’s Inc. (“Saucey” or “Company”)
        practices in connection with privacy and information that we collect
        through this website, the related mobile sites and mobile
        application(s), and selected other domains (the “Sites”). Our services
        and the Sites are only available to residents of the United States and
        you may not register for or use our services or Sites if you are not a
        resident of the United States. Except as indicated below, this Privacy
        Policy does not address the collection, use or disclosure of information
        by any of our affiliates or by other third parties on any websites other
        than the Sites.
      </Graf>
      <Graf>
        We may change this Privacy Policy from time to time. If we make a change
        to this Privacy Policy, it will be effective as soon as we post it, and
        the most current version of this Privacy Policy will always be posted
        under the Privacy Policy tab of this website. If we make a material
        change to this Privacy Policy, we will notify you by revising the date
        at the top of the Privacy Policy and, in some cases, we may provide you
        with additional notice (such as adding a statement to our homepage or
        sending you a notification). You agree that you will check out this
        Privacy Policy periodically. By continuing to access and/or use the
        Sites after we make changes to this Privacy Policy, you agree to the
        revised Privacy Policy. You agree that if you do not agree to the new
        terms of the Privacy Policy, you must stop using the Sites.
      </Graf>

      <Subheading>Notice of Information We Collect</Subheading>
      <Graf>
        In order for you to take advantage of particular opportunities provided
        through the Sites (for example, to use the services offered on the
        Sites), we may require that you furnish personal data. For example, we
        may ask you to complete a registration form that asks for contact
        information (such as name, date of birth, address, telephone number, and
        e-mail address) or an order form that asks for financial information
        (like credit card number, expiration date, and billing address).
        Additionally, when you order from the Sites we may collect certain
        consumer information (such as products ordered and relevant promotion
        codes) and delivery information (such as an alternate contact, address,
        and telephone number in the event you are not available to accept
        delivery). We will collect information that you provide directly to us,
        including if you communicate with us by e-mail, or otherwise complete
        online forms, surveys, or contest entries. You may choose not to provide
        us with certain personal data. In such an event, you can still access
        and view the Sites; however, you may not be able to order any products
        and the functionality of the Sites and services available to you may be
        limited. In addition, you can choose not to provide certain optional
        information, but then you might not be able to take full advantage of
        many of the features on the Sites.
      </Graf>
      <Graf>
        We may also collect certain personal data about you automatically,
        including information about the device you are using to access and/or
        use the Sites, log information (including your IP address, type of
        browser and operating system you use, etc.), information from referring
        websites, and your interaction with the Sites.
      </Graf>
      <Graf>
        We and our third party service providers also may aggregate personal
        data in a manner such that the end-product cannot be reasonably used to
        identify you or any other user of the Sites, for example, by using
        personal data to calculate the percentage of our users who have a
        particular telephone area code. We and our third party service providers
        may use and share this aggregated and de-identified data.
      </Graf>

      <Subheading>Cookies and Other Technology</Subheading>
      <Graf>
        A cookie is a small piece of data that is sent to your Internet browser
        and stored on your computer’s hard drive, and that delivers information
        about you and your activity on the Sites. Pixel tags and web beacons are
        small graphic files that can function in various ways (such as allowing
        us to track how you view an email that we send you and track your
        activity on the Sites) and are commonly used in conjunction with
        cookies. We, our third party service providers and other third parties
        detailed below under “How We Use The Information We Collect”, may use
        cookies, pixel tags, web beacons, and other similar technologies to
        better serve you with more tailored information and facilitate your
        ongoing use of the Sites. If you do not want information collected
        through the use of cookies, there is a simple procedure in most browsers
        that allows you to decline the use of cookies. To learn more about
        cookies, please visit the third party site{' '}
        <a href="http://www.allaboutcookies.org/">
          http://www.allaboutcookies.org/
        </a>
        . Please note that if you choose to remove or reject cookies, this could
        affect the availability and functionality of our Sites.
      </Graf>

      <Subheading>How We Use The Information We Collect</Subheading>
      <Graf>
        We and our third party service providers may use your personal data and
        any third party personal data that you may provide, as applicable:
      </Graf>
      <Paragraph tag="ul">
        <li>
          to fulfill your requests, e.g., to allow you to order liquor and other
          products offered through our partners and have it delivered to you or
          to provide you with information that you request.
        </li>
        <li>
          to fulfill your purchases (e.g., to process credit card payments in
          connection with your food order).
        </li>
        <li>
          to customize content on our Sites (e.g., the stores that will deliver
          to your location).
        </li>
        <li>
          to send you important information regarding the Sites, such as certain
          changes to our terms, conditions, policies and/or other administrative
          information.
        </li>
        <li>
          to inform you of products, programs, services and promotions that we
          believe may be of interest to you.
        </li>
        <li>
          to permit you to send messages regarding Site-related content to a
          friend through the Sites. If you wish to use this feature, you may be
          required to provide us with, and we may use, your friend’s email
          address to facilitate your sending of such message. By using this
          functionality, you affirm that you are entitled to use and provide us
          with your friend’s name and email address for this purpose. We will
          not to use your friend’s personal data for any purpose other than to
          allow your message to be sent and/or to troubleshoot any issues with
          respect to the sending of your message, unless we disclose such other
          purpose to you at the time you provide your friend’s personal data to
          us.
        </li>
        <li>
          to permit you to participate in sweepstakes, contests and similar
          promotions (collectively, “Promotions”).
        </li>
        <li>
          for our internal business purposes, such as data analysis, audits,
          developing new products, enhancing the Site, improving our services,
          identifying usage trends and determining the effectiveness of our
          promotional campaigns.
        </li>
      </Paragraph>

      <Subheading>Your California Privacy Rights</Subheading>
      <Graf>
        Under the California Consumer Privacy Act or CCPA, and subject to
        exceptions, California residents have certain rights regarding their
        data, including:
      </Graf>
      <Paragraph tag="ul">
        <li>
          The right to know the categories of Personal Information we’ve
          collected and the categories of sources from which we got the
          information.
        </li>
        <li>
          The right to know the business purposes for sharing Personal
          Information.
        </li>
        <li>
          The right to know the categories of third parties with whom we’ve
          shared Personal Information.
        </li>
        <li>
          The right to access the specific pieces of Personal Information we’ve
          collected and the right to delete your information.
        </li>
        <li>
          The right to opt out of having your Personal Information sold. Saucey
          does not sell or rent Personal Information to third parties, as we
          understand that term to be defined by the CCPA and its implementing
          regulations
        </li>
      </Paragraph>
      <Graf>
        As a California resident, you may exercise your right to know or your
        right to deletion by emailing support@saucey.com.
      </Graf>

      <Subheading>When & How We Share Your Information</Subheading>
      <Graf>
        We and our third party service providers may disclose personal data
        about you as follows or as otherwise described in this Privacy Policy:
      </Graf>
      <Paragraph tag="ul">
        <li>
          as required to fulfill your purchase (e.g. to our partner stores to
          enable them to fulfill your order).
        </li>
        <li>
          to provide services such as payment processing, website hosting, data
          analysis, infrastructure provision, IT services, customer service,
          e-mail delivery services, and other similar services, to enable them
          to provide services.
        </li>
        <li>
          to identify you to any person to whom you send messages through the
          Sites.
        </li>
        <li>
          to communicate with you about Promotions, products, services, rewards
          and events offered by us or others. You should carefully review the
          rules, if any, of each Promotion in which you participate, as they may
          contain additional important information about how such third parties
          use your personal data.
        </li>
        <li>
          to an affiliate or other third party in the event of any
          reorganization, merger, sale, joint venture, assignment, transfer or
          other disposition of all or any portion of our business, assets or
          stock (including without limitation in connection with any bankruptcy
          and/or similar proceedings).
        </li>
        <li>
          as we believe to be appropriate: (a) under applicable law, including
          laws outside your country of residence; (b) to comply with legal
          process; (c) to respond to requests from public and government
          authorities; (d) to enforce our terms and conditions; (e) to protect
          our operations and/or those of any of our affiliates; (f) to protect
          our rights, privacy, safety and/or property, and/or that of our
          affiliates, you and/or others; (g) to permit us to pursue available
          remedies and/or limit the damages that we may sustain; and (h) for
          record keeping purposes.
        </li>
      </Paragraph>

      <Subheading>
        Other Important Notices Regarding Our Privacy Practices
      </Subheading>
      <Graf>
        Third Party Sites. Except as expressly set forth herein, this Privacy
        Policy does not address, and neither we nor our third party service
        providers are responsible for, the privacy, information or other
        practices of other websites and/or any third parties, including without
        limitation any of our affiliates and/or any third party operating any
        site to which the Sites contains a link. The inclusion of a link to a
        third party website on the Sites does not imply endorsement of the
        linked site by us or by our affiliates. Please note that your access
        and/or use of any third party websites, including by providing any
        information, materials and/or other content to such sites, is entirely
        at your own risk.
      </Graf>
      <Graf>
        Unsubscribe. If you do not wish to receive marketing-related emails from
        us, you may unsubscribe from receiving them by clicking on the
        “unsubscribe” link at the bottom of any of those emails. If you opt out,
        we may still send you non-promotional emails, such as those about your
        account or our ongoing business relations.
      </Graf>
      <Graf>
        Changing personal data. You may review, correct and/or update certain
        elements of your personal data by adjusting your preferences in the “My
        Account” section of our site, but note that we may retain certain
        information as required by law or for legitimate business purposes. We
        and our third party service providers are not responsible for altering
        personal data from the databases and/or other records of third parties
        with whom we and our third party service providers have shared your
        personal data.
      </Graf>
      <Graf>
        Retention Period. We and our third party service providers will retain
        personal data for the period reasonably necessary to fulfill the
        purposes outlined in this Privacy Policy, unless a longer retention
        period is required or permitted by law.
      </Graf>
      <Graf>
        Note Regarding the Use of the Site by Minors. The Sites are not directed
        to and/or intended for use by individuals under the age of twenty-one
        (21), and we request that such individuals not use the Sites and not
        provide personal data through the Sites.
      </Graf>
      <Graf>
        Advertising and Analytics Services Provided by Others. We may allow
        others to provide analytics services and serve advertisements on our
        behalf across the internet and in applications. These entities may use
        cookies, web beacons, device identifiers and other technologies to
        collect information about your use of the Sites and other websites and
        applications, including your IP address, web browser, mobile network
        information, pages viewed, time spent on pages or in apps, links clicked
        and conversion information. This information may be used by Company and
        others to, among other things, analyze and track data, determine the
        popularity of certain content, deliver advertising and content targeted
        to your interests on our Sites and other websites and better understand
        your online activity. For more information about interest-based ads, or
        to opt out of having your web browsing information used for behavioral
        advertising purposes, please visit www.aboutads.info/choices.
      </Graf>
      <Graf>
        Disclaimer. If you choose to access the Sites, you do so at your own
        risk. We may limit the availability of the Sites, in whole or in part,
        to any person, geographic area and/or jurisdiction we choose, at any
        time and in our sole discretion. By using the Sites and submitting any
        personal data, you consent to the transfer of personal data to other
        countries, such as the United States, which may provide a different
        level of data security than your country of residence provides. We will
        take steps to ensure that your personal data receives an adequate level
        of protection in the jurisdictions in which we process it.
      </Graf>
      <Graf>
        Contacting Us. If you have any questions regarding this Privacy Policy,
        please contact us by e-mail at support@saucey.com.
      </Graf>
    </Article>
  );
}
